import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, Euro, Clock, CheckCircle, AlertCircle, FileText, BarChart3, TrendingUp, Eye, Plus, Upload, X, Circle, Building2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import InfluencerAvatar from '../components/InfluencerAvatar';
import CustomListbox from '../components/CustomListbox';

const encodeSupabaseUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    const encodedParts = pathParts.map((part, index) =>
      index === pathParts.length - 1 ? encodeURIComponent(part) : part
    );
    urlObj.pathname = encodedParts.join('/');
    return urlObj.toString();
  } catch {
    return url;
  }
};

const sanitizeFileName = (fileName: string): string => {
  return fileName
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_');
};

interface CollabData {
  id: string;
  name: string;
  description?: string;
  date: string;
  budget: number;
  status: string;
  platform?: string;
  client_name?: string;
  influencers?: any[];
  brand?: string;
}

interface TimelineItem {
  id?: string;
  step_name: string;
  deadline?: string;
  status: 'todo' | 'in_progress' | 'done' | 'late';
  order_index: number;
  influencer_id?: string;
  influencer_name?: string;
}

interface Document {
  id: string;
  name: string;
  type: string;
  url?: string;
  file_url?: string;
  created_at: string;
  influencer_id?: string;
}

interface Stat {
  id: string;
  creator: string;
  views: number;
  likes: number;
  engagement_rate: number;
  post_date?: string;
}

export default function PublicCollaborationView() {
  const { id } = useParams<{ id: string }>();
  const [collab, setCollab] = useState<CollabData | null>(null);
  const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [stats, setStats] = useState<Stat[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('timeline');
  const [isAddingTimeline, setIsAddingTimeline] = useState(false);
  const [isAddingDocument, setIsAddingDocument] = useState(false);
  const [newTimeline, setNewTimeline] = useState({ step_name: '', deadline: '', status: 'todo' as const });
  const [newDocument, setNewDocument] = useState({ name: '', type: 'brief', file: null as File | null });
  const [isUploading, setIsUploading] = useState(false);
  const [currentInfluencerId, setCurrentInfluencerId] = useState<string | null>(null);

  useEffect(() => {
    loadCollaborationData();
  }, [id]);

  const loadCollaborationData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: collabData, error: collabError } = await supabase
        .from('collabs')
        .select('*')
        .eq('id', id!)
        .maybeSingle();

      if (collabError || !collabData) {
        setError("Cette collaboration n'existe pas");
        setIsLoading(false);
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const influencerIdFromUrl = urlParams.get('influencerId');
      setCurrentInfluencerId(influencerIdFromUrl);

      if (collabData.influencers && collabData.influencers.length > 0) {
        const influencerIds = collabData.influencers
          .map((inf: any) => inf.id)
          .filter((id: string) => id && id.trim() !== '');

        if (influencerIds.length > 0) {
          const { data: influencerProfiles } = await supabase
            .from('influencers')
            .select('id, display_name, full_name, profile_image_url, username, followers_count')
            .in('id', influencerIds);

          if (influencerProfiles && influencerProfiles.length > 0) {
            collabData.influencers = collabData.influencers.map((inf: any) => {
              const profile = influencerProfiles.find(p => p.id === inf.id);
              if (profile) {
                return {
                  ...inf,
                  fullName: inf.full_name || inf.fullName || profile.full_name || profile.display_name,
                  profileImageUrl: inf.profile_image_url || inf.profileImageUrl || profile.profile_image_url,
                  username: profile.username,
                  followersCount: profile.followers_count,
                  rate: inf.rate || inf.budget || 0
                };
              }
              return inf;
            });
          }
        }
      }

      setCollab(collabData);

      const { data: timelineData } = await supabase
        .from('timeline_items')
        .select('*')
        .eq('collab_id', id)
        .order('order_index', { ascending: true });

      if (timelineData) setTimelineItems(timelineData);

      const { data: documentsData } = await supabase
        .from('collab_documents')
        .select('*')
        .eq('collab_id', id)
        .order('created_at', { ascending: false });

      if (documentsData) setDocuments(documentsData);

      const { data: statsData } = await supabase
        .from('collab_stats')
        .select('*')
        .eq('collab_id', id);

      if (statsData) setStats(statsData);

    } catch {
      setError('Une erreur est survenue lors du chargement des données');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTimeline = async () => {
    if (!newTimeline.step_name.trim()) return;
    try {
      setIsUploading(true);
      const currentInfluencer = collab?.influencers?.find(i => i.id === currentInfluencerId);
      const influencerName = currentInfluencer?.fullName || currentInfluencer?.full_name;
      const { data, error } = await supabase
        .from('timeline_items')
        .insert([{
          collab_id: id,
          step_name: newTimeline.step_name,
          deadline: newTimeline.deadline || null,
          status: newTimeline.status,
          order_index: timelineItems.length,
          influencer_id: currentInfluencerId,
          influencer_name: influencerName
        }])
        .select()
        .single();
      if (error) throw error;
      setTimelineItems([...timelineItems, data]);
      setNewTimeline({ step_name: '', deadline: '', status: 'todo' });
      setIsAddingTimeline(false);
    } catch {
      alert("Erreur lors de l'ajout de l'étape");
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddDocument = async () => {
    if (!newDocument.name.trim() || !newDocument.file) return;
    if (newDocument.file.size > 500 * 1024 * 1024) {
      alert('Le fichier est trop volumineux. La taille maximale est de 500 MB.');
      return;
    }
    try {
      setIsUploading(true);
      const sanitizedFileName = sanitizeFileName(newDocument.file.name);
      const fileExt = sanitizedFileName.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `collaboration-documents/${id}/${fileName}`;
      const { error: uploadError } = await supabase.storage.from('resources').upload(filePath, newDocument.file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('resources').getPublicUrl(filePath);
      const currentInfluencer = collab?.influencers?.find(i => i.id === currentInfluencerId);
      const influencerName = currentInfluencer?.fullName || currentInfluencer?.full_name;
      const { data, error } = await supabase
        .from('collab_documents')
        .insert([{
          collab_id: id,
          name: newDocument.name,
          type: newDocument.type,
          file_url: publicUrl,
          file_name: newDocument.file.name,
          file_size: newDocument.file.size.toString(),
          influencer_id: currentInfluencerId,
          influencer_name: influencerName
        }])
        .select()
        .single();
      if (error) throw error;
      setDocuments([data, ...documents]);
      setNewDocument({ name: '', type: 'brief', file: null });
      setIsAddingDocument(false);
    } catch {
      alert("Erreur lors de l'ajout du document");
    } finally {
      setIsUploading(false);
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const getTimelineStatusConfig = (status: string) => {
    switch (status) {
      case 'done':
        return {
          dot: 'border-emerald-500 bg-emerald-500/20',
          icon: <CheckCircle className="w-4 h-4 text-emerald-400" />,
          pill: 'bg-emerald-950/60 text-emerald-400 border border-emerald-800/60',
          label: 'Terminé'
        };
      case 'in_progress':
        return {
          dot: 'border-amber-500 bg-amber-500/20 animate-pulse',
          icon: <Clock className="w-4 h-4 text-amber-400" />,
          pill: 'bg-amber-950/60 text-amber-400 border border-amber-800/60',
          label: 'En cours'
        };
      case 'late':
        return {
          dot: 'border-red-500 bg-red-500/20',
          icon: <AlertCircle className="w-4 h-4 text-red-400" />,
          pill: 'bg-red-950/60 text-red-400 border border-red-800/60',
          label: 'En retard'
        };
      default:
        return {
          dot: 'border-[#444] bg-[#2A2A2A]',
          icon: <Circle className="w-4 h-4 text-[#666]" />,
          pill: 'bg-[#2A2A2A] text-[#888] border border-[#444]',
          label: 'À faire'
        };
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#888888] text-sm tracking-wide">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !collab) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center bg-[#1A1A1A] border border-[#2A2A2A] p-8 rounded-2xl max-w-md">
          <div className="w-12 h-12 rounded-full bg-[#2A2A2A] flex items-center justify-center mx-auto mb-4">
            <span className="text-[#888888] text-xl">?</span>
          </div>
          <h2 className="text-xl font-semibold text-[#FAFAFA] mb-2">Page introuvable</h2>
          <p className="text-[#888888] text-sm">{error || 'Une erreur est survenue'}</p>
        </div>
      </div>
    );
  }

  const totalStats = {
    views: stats.reduce((sum, s) => sum + (s.views || 0), 0),
    likes: stats.reduce((sum, s) => sum + (s.likes || 0), 0),
    avgEngagement: stats.length > 0
      ? stats.reduce((sum, s) => sum + (s.engagement_rate || 0), 0) / stats.length
      : 0
  };

  const visibleDocuments = documents.filter(doc =>
    !currentInfluencerId || !doc.influencer_id || doc.influencer_id === currentInfluencerId
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-10 px-4 sm:px-6">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        .font-serif-display { font-family: 'Inter', system-ui, sans-serif; }
        .font-inter { font-family: 'Inter', system-ui, sans-serif; }
        .card-hover { transition: transform 200ms ease, border-color 200ms ease, box-shadow 200ms ease; }
        .card-hover:hover { transform: translateY(-2px); border-color: rgba(212,175,55,0.25) !important; box-shadow: 0 8px 32px rgba(0,0,0,0.4); }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .fade-up { animation: fadeUp 0.5s ease forwards; }
        .fade-up-1 { animation-delay: 0.05s; opacity: 0; }
        .fade-up-2 { animation-delay: 0.12s; opacity: 0; }
        .fade-up-3 { animation-delay: 0.2s; opacity: 0; }
        .fade-up-4 { animation-delay: 0.28s; opacity: 0; }
        input[type="text"], input[type="date"], input[type="file"] {
          background: #111111;
          border-color: #2A2A2A;
          color: #FAFAFA;
        }
        input[type="text"]:focus, input[type="date"]:focus {
          border-color: #D4AF37 !important;
          outline: none;
          box-shadow: 0 0 0 2px rgba(212,175,55,0.15);
        }
      `}</style>

      <div className="max-w-3xl mx-auto space-y-5 font-inter">

        {/* Header */}
        <div className="fade-up fade-up-1 bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl p-6 sm:p-8">
          <div className="mb-6">
            <h1 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#FAFAFA] leading-tight mb-2">
              {collab.name}
            </h1>
            {collab.client_name && (
              <p className="text-[#888888] text-sm flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                {collab.client_name}
              </p>
            )}
          </div>

          {collab.description && (
            <p className="text-[#888888] text-sm leading-relaxed mb-6 pb-6 border-b border-[#2A2A2A]">
              {collab.description}
            </p>
          )}

          {/* Date + Tarif */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            <div className="bg-[#111111] border border-[#2A2A2A] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-xs font-medium text-[#888888] uppercase tracking-wide">Date</span>
              </div>
              <div className="text-base font-semibold text-[#FAFAFA]">
                {formatDate(collab.date)}
              </div>
            </div>
            <div className="bg-[#111111] border border-[#2A2A2A] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Euro className="w-4 h-4 text-emerald-400" />
                <span className="text-xs font-medium text-[#888888] uppercase tracking-wide">Tarif</span>
              </div>
              <div className="text-xl font-bold text-[#FAFAFA]">
                {currentInfluencerId && collab.influencers ? (
                  (() => {
                    const inf = collab.influencers.find((i: any) => i.id === currentInfluencerId);
                    return inf?.rate ? `${parseFloat(inf.rate).toLocaleString('fr-FR')} €` : 'Non défini';
                  })()
                ) : (
                  `${collab.budget?.toLocaleString('fr-FR')} €`
                )}
              </div>
            </div>
          </div>

          {/* Profil influenceur */}
          {currentInfluencerId && collab.influencers && collab.influencers.length > 0 && (
            <div className="border-t border-[#2A2A2A] pt-5">
              <p className="text-xs font-medium text-[#888888] uppercase tracking-wide mb-3">Votre profil</p>
              <div className="flex flex-wrap gap-3">
                {collab.influencers
                  .filter((inf: any) => inf.id === currentInfluencerId)
                  .map((inf: any) => (
                    <div key={inf.id} className="flex items-center gap-3 bg-[#111111] border border-[#2A2A2A] rounded-xl p-3">
                      <InfluencerAvatar
                        profileImageUrl={inf.profileImageUrl}
                        name={inf.fullName}
                        size="md"
                      />
                      <div>
                        <div className="font-semibold text-[#FAFAFA] text-sm">{inf.fullName}</div>
                        {inf.username && <div className="text-xs text-[#888888]">@{inf.username}</div>}
                        {inf.followersCount && (
                          <div className="text-xs text-[#888888]">{formatNumber(inf.followersCount)} abonnés</div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Stats */}
        {stats.length > 0 && (
          <div className="fade-up fade-up-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl p-6">
            <h2 className="font-serif-display text-lg font-semibold text-[#FAFAFA] mb-5 flex items-center gap-3">
              <span className="w-7 h-7 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
                <BarChart3 className="w-3.5 h-3.5 text-[#D4AF37]" />
              </span>
              Performance globale
            </h2>
            <div className="grid grid-cols-3 gap-3">
              {[
                { icon: Eye, value: formatNumber(totalStats.views), label: 'Vues' },
                { icon: CheckCircle, value: formatNumber(totalStats.likes), label: 'Likes' },
                { icon: TrendingUp, value: `${totalStats.avgEngagement.toFixed(1)}%`, label: 'Engagement' },
              ].map(({ icon: Icon, value, label }) => (
                <div key={label} className="bg-[#111111] border border-[#2A2A2A] rounded-xl p-4 text-center">
                  <Icon className="w-4 h-4 text-[#888888] mx-auto mb-2" />
                  <div className="text-lg font-bold text-[#FAFAFA]">{value}</div>
                  <div className="text-xs text-[#888888] mt-0.5">{label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="fade-up fade-up-3 bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl overflow-hidden">
          {/* Tab bar */}
          <div className="flex border-b border-[#2A2A2A]">
            {[
              { key: 'timeline', icon: Clock, label: 'Timeline' },
              { key: 'documents', icon: FileText, label: `Documents et Preview (${visibleDocuments.length})` },
            ].map(({ key, icon: Icon, label }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-4 text-sm font-medium transition-colors duration-200 ${
                  activeTab === key
                    ? 'text-[#D4AF37] border-b-2 border-[#D4AF37] -mb-px'
                    : 'text-[#888888] hover:text-[#FAFAFA] border-b-2 border-transparent -mb-px'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{label}</span>
                <span className="sm:hidden">{key === 'timeline' ? 'Timeline' : 'Docs'}</span>
              </button>
            ))}
          </div>

          <div className="p-5 sm:p-7">
            {/* Timeline tab */}
            {activeTab === 'timeline' && (
              <div>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
                  <h3 className="text-base font-semibold text-[#FAFAFA]">Étapes de la campagne</h3>
                  <button
                    onClick={() => setIsAddingTimeline(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] rounded-lg hover:bg-[#D4AF37]/20 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Ajouter une étape
                  </button>
                </div>

                {isAddingTimeline && (
                  <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-xl p-4 mb-5">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-medium text-[#FAFAFA] text-sm">Nouvelle étape</h4>
                      <button onClick={() => setIsAddingTimeline(false)} className="text-[#888888] hover:text-[#FAFAFA]">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <input
                        type="text"
                        placeholder="Nom de l'étape"
                        value={newTimeline.step_name}
                        onChange={(e) => setNewTimeline({ ...newTimeline, step_name: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                      <input
                        type="date"
                        value={newTimeline.deadline}
                        onChange={(e) => setNewTimeline({ ...newTimeline, deadline: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                      <button
                        onClick={handleAddTimeline}
                        disabled={isUploading || !newTimeline.step_name.trim()}
                        className="w-full px-4 py-2 bg-[#D4AF37] text-[#0A0A0A] rounded-lg hover:bg-[#c9a832] transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isUploading ? 'Ajout...' : 'Ajouter'}
                      </button>
                    </div>
                  </div>
                )}

                {timelineItems.length === 0 ? (
                  <div className="text-center py-12">
                    <Clock className="w-10 h-10 text-[#2A2A2A] mx-auto mb-3" />
                    <p className="text-[#888888] text-sm">Aucune étape pour le moment</p>
                  </div>
                ) : (
                  <div className="space-y-8">
                    {(() => {
                      const filteredItems = currentInfluencerId
                        ? timelineItems.filter(item => !item.influencer_id || item.influencer_id === currentInfluencerId)
                        : timelineItems;

                      const groupedItems = filteredItems.reduce((acc, item) => {
                        const key = item.influencer_id || 'common';
                        if (!acc[key]) acc[key] = [];
                        acc[key].push(item);
                        return acc;
                      }, {} as Record<string, typeof filteredItems>);

                      const commonItems = groupedItems['common'] || [];
                      const influencerGroups = Object.entries(groupedItems).filter(([key]) => key !== 'common');

                      const renderItems = (items: typeof filteredItems) => (
                        <div className="space-y-3">
                          {items.map((item, index) => {
                            const cfg = getTimelineStatusConfig(item.status);
                            return (
                              <div key={item.id} className="relative flex items-start gap-4">
                                {index < items.length - 1 && (
                                  <div className="absolute top-8 left-[15px] bottom-0 w-px bg-[#2A2A2A]" />
                                )}
                                <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${cfg.dot}`}>
                                  {cfg.icon}
                                </div>
                                <div className="flex-1 bg-[#111111] border border-[#2A2A2A] rounded-xl p-4 card-hover">
                                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-1">
                                    <h4 className="font-medium text-[#FAFAFA] text-sm">{item.step_name}</h4>
                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${cfg.pill}`}>
                                      {cfg.icon && <span className="w-3 h-3">{cfg.icon}</span>}
                                      {cfg.label}
                                    </span>
                                  </div>
                                  {item.deadline && (
                                    <p className="text-xs text-[#888888] flex items-center gap-1.5 mt-1">
                                      <Calendar className="w-3.5 h-3.5" />
                                      Échéance : {formatDate(item.deadline)}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );

                      return (
                        <>
                          {commonItems.length > 0 && (
                            <div>
                              {influencerGroups.length > 0 && (
                                <p className="text-xs font-medium uppercase tracking-widest text-[#888888] mb-4">
                                  Étapes communes
                                </p>
                              )}
                              {renderItems(commonItems)}
                            </div>
                          )}
                          {influencerGroups.map(([infId, items]) => {
                            const influencer = collab?.influencers?.find((inf: any) => inf.id === infId);
                            const influencerName = influencer?.fullName || influencer?.full_name || items[0]?.influencer_name || 'Créateur';
                            const profileImageUrl = influencer?.profileImageUrl || influencer?.profile_image_url || null;
                            return (
                              <div key={infId}>
                                <div className="flex items-center gap-3 mb-4">
                                  {influencer && (
                                    <InfluencerAvatar
                                      profileImageUrl={profileImageUrl}
                                      name={influencerName}
                                      size="sm"
                                    />
                                  )}
                                  <p className="text-xs font-semibold uppercase tracking-widest text-[#888888]">
                                    {influencerName}
                                  </p>
                                </div>
                                {renderItems(items)}
                              </div>
                            );
                          })}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* Documents tab */}
            {activeTab === 'documents' && (
              <div>
                <div className="flex justify-between items-center mb-5">
                  <h3 className="text-base font-semibold text-[#FAFAFA]">Documents et Preview</h3>
                  <button
                    onClick={() => setIsAddingDocument(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] rounded-lg hover:bg-[#D4AF37]/20 transition-colors text-sm"
                  >
                    <Upload className="w-4 h-4" />
                    Ajouter
                  </button>
                </div>

                {isAddingDocument && (
                  <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-xl p-4 mb-5">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-medium text-[#FAFAFA] text-sm">Nouveau document</h4>
                      <button onClick={() => setIsAddingDocument(false)} className="text-[#888888] hover:text-[#FAFAFA]">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <input
                        type="text"
                        placeholder="Nom du document"
                        value={newDocument.name}
                        onChange={(e) => setNewDocument({ ...newDocument, name: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                      <CustomListbox
                        value={newDocument.type}
                        onChange={(value) => setNewDocument({ ...newDocument, type: value })}
                        options={[
                          { value: 'brief', label: 'Brief' },
                          { value: 'preview', label: 'Preview' },
                          { value: 'contract', label: 'Contrat' },
                          { value: 'other', label: 'Autre' }
                        ]}
                      />
                      <input
                        type="file"
                        onChange={(e) => setNewDocument({ ...newDocument, file: e.target.files?.[0] || null })}
                        className="w-full px-3 py-2 border rounded-lg text-sm text-[#888888]"
                      />
                      <button
                        onClick={handleAddDocument}
                        disabled={isUploading || !newDocument.name.trim() || !newDocument.file}
                        className="w-full px-4 py-2 bg-[#D4AF37] text-[#0A0A0A] rounded-lg hover:bg-[#c9a832] transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isUploading ? 'Upload...' : 'Ajouter'}
                      </button>
                    </div>
                  </div>
                )}

                {visibleDocuments.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-10 h-10 text-[#2A2A2A] mx-auto mb-3" />
                    <p className="text-[#888888] text-sm">Aucun document pour le moment</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {visibleDocuments.map((doc) => (
                      <div key={doc.id} className="bg-[#111111] border border-[#2A2A2A] rounded-xl p-4 flex items-center justify-between card-hover">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-[#2A2A2A] flex items-center justify-center flex-shrink-0">
                            <FileText className="w-4 h-4 text-[#888888]" />
                          </div>
                          <div>
                            <h4 className="font-medium text-[#FAFAFA] text-sm">{doc.name}</h4>
                            <p className="text-xs text-[#888888]">{doc.type}</p>
                          </div>
                        </div>
                        {doc.file_url && (
                          <a
                            href={encodeSupabaseUrl(doc.file_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-1.5 bg-[#FAFAFA]/10 border border-[#FAFAFA]/10 text-[#FAFAFA] rounded-lg hover:bg-[#D4AF37]/20 hover:border-[#D4AF37]/30 hover:text-[#D4AF37] transition-colors text-xs font-medium flex-shrink-0"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            Voir
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="fade-up fade-up-4 text-center pb-6">
          <p className="text-[#888888]/50 text-xs tracking-widest uppercase">Propulsé par Campaly</p>
        </div>
      </div>
    </div>
  );
}
